import { useMemo } from "react"
import { useQuery } from "urql"

import "../graphql/queries"
import { MIN_SEARCH_LENGTH } from "@/constants/common"
import {
  GetProductInventorySuggestionListDocument,
  InventoryByProductListSortField,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface UseGetProductInventorySuggestionsParams {
  query: string
}

interface UseGetProductInventorySuggestionsReturn {
  productOptions: Option<string>[]
  isFetching: boolean
}

export const useGetProductInventorySuggestions = ({
  query,
}: UseGetProductInventorySuggestionsParams): UseGetProductInventorySuggestionsReturn => {
  const [{ data, fetching }] = useQuery({
    query: GetProductInventorySuggestionListDocument,
    variables: {
      input: {
        filter: { query },
        sort: { field: InventoryByProductListSortField.Description, order: SortingOrder.Asc },
        limit: 50,
        offset: 0,
      },
    },
    pause: query.length < MIN_SEARCH_LENGTH,
  })

  const productOptions = useMemo<Option<string>[]>(() => {
    return data?.inventory.listByProduct.__typename === "InventoryByProductListSuccess"
      ? data.inventory.listByProduct.items.map((productInventory) => {
          const product = productInventory.product
          const displayId = product.externalSystemId
            ? `${product.publicId} / ${product.externalSystemId}`
            : product.publicId
          return {
            value: product.productId.toString(),
            label: `${displayId} - ${product.companyProductDescription}`,
          }
        })
      : []
  }, [data])

  return { productOptions, isFetching: fetching }
}
